import { create } from "zustand";
import { persist } from "zustand/middleware";
import { USER_PREFERENCE_KEY_PREFIX } from "@/lib/auth/clearAppStorage";

type ReleaseNotesState = {
  /**
   * Id of the newest release note the user has already seen. The profile menu
   * shows an unread dot until no visible note is newer than this. Per-viewer,
   * per browser (localStorage) — a "what's new" hint, not synced state.
   */
  lastSeenReleaseNoteId: string | null;
  markReleaseNotesSeen: (newestReleaseNoteId: string) => void;
  /**
   * Whether the changelog lists technical releases (CI, dependencies,
   * infrastructure). Off by default: most users only want what they can see.
   */
  showTechnicalReleaseNotes: boolean;
  setShowTechnicalReleaseNotes: (show: boolean) => void;
  /**
   * Whether the changelog dialog is open. Lives here, not in the profile menu,
   * because the menu sits in the sidebar and the sidebar is a different React
   * subtree below the `md` breakpoint (a sheet): resizing across it unmounted
   * the menu and took an open dialog with it. The dialog itself mounts once in
   * the router, beside the sidebar. Not persisted.
   */
  isDialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
};

/**
 * Under the preference prefix so the deploy-time storage wipe keeps it. The
 * old `release-notes-storage` key was wiped on every deploy, which brought the
 * unread dot back a few times a week.
 */
export const RELEASE_NOTES_STORAGE_KEY = `${USER_PREFERENCE_KEY_PREFIX}release-notes`;

const useReleaseNotesStore = create<ReleaseNotesState>()(
  persist(
    (set) => ({
      lastSeenReleaseNoteId: null,
      markReleaseNotesSeen: (newestReleaseNoteId: string) =>
        set({ lastSeenReleaseNoteId: newestReleaseNoteId }),
      showTechnicalReleaseNotes: false,
      setShowTechnicalReleaseNotes: (show: boolean) =>
        set({ showTechnicalReleaseNotes: show }),
      isDialogOpen: false,
      setDialogOpen: (open: boolean) => set({ isDialogOpen: open }),
    }),
    {
      name: RELEASE_NOTES_STORAGE_KEY,
      partialize: (state) => ({
        lastSeenReleaseNoteId: state.lastSeenReleaseNoteId,
        showTechnicalReleaseNotes: state.showTechnicalReleaseNotes,
      }),
    }
  )
);

export default useReleaseNotesStore;
